import { Routes, Route, Navigate } from 'react-router-dom'
import { Toaster } from 'react-hot-toast'
import { AuthProvider, useAuth } from '@/hooks/useAuth'
import { TenantProvider } from '@/hooks/useTenant'
import { RoleProvider } from '@/hooks/useRole'
import { TrailingSlashRedirect } from '@/components/TrailingSlashRedirect'

// Layouts
import DashboardLayout from '@/components/layouts/DashboardLayout'
import AuthLayout from '@/components/layouts/AuthLayout'

// Auth Pages
import LoginPage from '@/pages/auth/LoginPage'
import RegisterPage from '@/pages/auth/RegisterPage'
import ForgotPasswordPage from '@/pages/auth/ForgotPasswordPage'
import VerifyPage from '@/pages/auth/VerifyPage'

// Dashboard Pages
import DashboardPage from '@/pages/dashboard/DashboardPage'
import { InboxPageNew as InboxPage } from '@/pages/dashboard/InboxPageNew'
import ConversationsPage from '@/pages/dashboard/InboxPage'
import SettingsPage from '@/pages/dashboard/SettingsPage'
import AnalyticsPage from '@/pages/dashboard/AnalyticsPage'
import IntegrationsPage from '@/pages/dashboard/IntegrationsPage'
import AgentsPage from '@/pages/dashboard/AgentsPage'
import AISettingsPage from '@/pages/dashboard/AISettingsPage'
import KnowledgeBasePage from '@/pages/dashboard/KnowledgeBasePage'
import AdminPage from '@/pages/dashboard/AdminPage'
import TeamPage from '@/pages/dashboard/TeamPage'

// Protected Route Component
function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    )
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />
  }
  
  return <>{children}</>
}

// Super Admin Route Guard
function SuperAdminRoute({ children }: { children: React.ReactNode }) {
  const { isSuperAdmin } = useAuth()
  if (!isSuperAdmin) {
    return <Navigate to="/dashboard" replace />
  }
  return <>{children}</>
}

// Public Route (redirect if authenticated)
function PublicRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth()
  
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500"></div>
      </div>
    )
  }
  
  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />
  }
  
  return <>{children}</>
}

export default function App() {
  return (
    <AuthProvider>
      <TenantProvider>
        <RoleProvider>
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: { fontSize: '14px' },
            success: { iconTheme: { primary: '#10b981', secondary: '#fff' } },
            error: { duration: 5000 },
          }}
        />
        <TrailingSlashRedirect />
        <Routes>
        {/* Public Auth Routes */}
        <Route element={<AuthLayout />}>
          <Route path="/login" element={
            <PublicRoute><LoginPage /></PublicRoute>
          } />
          <Route path="/register" element={
            <PublicRoute><RegisterPage /></PublicRoute>
          } />
          <Route path="/forgot-password" element={
            <PublicRoute><ForgotPasswordPage /></PublicRoute>
          } />
          <Route path="/verify" element={
            <PublicRoute><VerifyPage /></PublicRoute>
          } />
        </Route>
        
        {/* Protected Dashboard Routes */}
        <Route element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }>
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/inbox" element={<InboxPage />} />
          <Route path="/conversations" element={<SuperAdminRoute><ConversationsPage /></SuperAdminRoute>} />
          <Route path="/agents" element={<AgentsPage />} />
          <Route path="/ai-settings" element={<SuperAdminRoute><AISettingsPage /></SuperAdminRoute>} />
          <Route path="/knowledge-base" element={<SuperAdminRoute><KnowledgeBasePage /></SuperAdminRoute>} />
          <Route path="/analytics" element={<AnalyticsPage />} />
          <Route path="/integrations" element={<SuperAdminRoute><IntegrationsPage /></SuperAdminRoute>} />
          <Route path="/team" element={<TeamPage />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/admin" element={<SuperAdminRoute><AdminPage /></SuperAdminRoute>} />
          {/* Redirects for old routes */}
          <Route path="/tickets" element={<Navigate to="/inbox" replace />} />
          <Route path="/router" element={<Navigate to="/ai-settings" replace />} />
          <Route path="/priority" element={<Navigate to="/ai-settings" replace />} />
        </Route>
        
        {/* Default redirects */}
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
        </RoleProvider>
      </TenantProvider>
    </AuthProvider>
  )
}
